/* eslint-disable no-magic-numbers */
import { EventEmitter } from "events";
import ScreenSizeDetector from "screen-size-detector";
import debugFactory from "debug";

const debug = debugFactory( "teaming:slice-engine:foundation:screen" );

export const screen = Object.create( new EventEmitter() );

const BREAKPOINTS = screen.BREAKPOINTS = Object.freeze( {
	XS: "xs",
	SM: "sm",
	MD: "md",
	LG: "lg",
	XL: "xl",
} );

const ranges = screen.ranges = Object.freeze( {
	[ BREAKPOINTS.XS ]: [ 0, 599, ],
	[ BREAKPOINTS.SM ]: [ 600, 959, ],
	[ BREAKPOINTS.MD ]: [ 960, 1239, ],
	[ BREAKPOINTS.LG ]: [ 1240, 1439, ],
	[ BREAKPOINTS.XL ]: [ 1440, Number.MAX_SAFE_INTEGER, ],
} );

screen.findBreakpoint = width => {
	for ( const key of Object.keys( ranges ) ) {
		const range = ranges[ key ];
		if ( width >= range[ 0 ] && width <= range[ 1 ] ) {
			return key;
		}
	}
	return "";
};

screen.lt = ( width, breakpoint ) => {
	const breakpointRange = ranges[ breakpoint ];
	return width < breakpointRange[ 0 ];
};

screen.gt = ( width, breakpoint ) => {
	const breakpointRange = ranges[ breakpoint ];
	return width > breakpointRange[ 1 ];
};

screen.lteq = ( width, breakpoint ) => {
	const breakpointRange = ranges[ breakpoint ];
	return width <= breakpointRange[ 1 ];
};

screen.gteq = ( width, breakpoint ) => {
	const breakpointRange = ranges[ breakpoint ];
	return width >= breakpointRange[ 0 ];
};

screen.eq = ( width, breakpoint ) => {
	return screen.findBreakpoint( width ) === breakpoint;
};

const emitSize = ( size, options = {} ) => {
	return () => {
		debug( "breakpoint reached", size, options );
		screen.emit( size, options );
	};
};

const detector = new ScreenSizeDetector( {
	onHeightChange: () => {
		const { width, height, } = detector;
		debug( "height change", height );
		screen.emit( "heightChange", height );
		screen.emit( "resize", { width, height, } );
	},
	onWidthChange: () => {
		const { width, height, } = detector;
		debug( "width change", width );
		screen.emit( "widthChange", width );
		screen.emit( "resize", { width, height, } );
	},
	onBothChange: () => {
		const { width, height, } = detector;
		debug( "both change", { width, height, } );
		screen.emit( "onBothChange", { width, height, } );
	},
	widthDefinitions: {
		[ BREAKPOINTS.XS ]: {
			min: 0,
			max: 599,
			inclusion: "[]",
			onEnter: emitSize( BREAKPOINTS.XS, {
				min: ranges[ BREAKPOINTS.XS ][ 0 ],
				max: ranges[ BREAKPOINTS.XS ][ 1 ],
			} ),
		},
		[ BREAKPOINTS.SM ]: {
			min: 600,
			max: 959,
			inclusion: "[]",
			onEnter: emitSize( BREAKPOINTS.SM, {
				min: ranges[ BREAKPOINTS.SM ][ 0 ],
				max: ranges[ BREAKPOINTS.SM ][ 1 ],
			} ),
		},
		[ BREAKPOINTS.MD ]: {
			min: 960,
			max: 1239,
			inclusion: "[]",
			onEnter: emitSize( BREAKPOINTS.MD, {
				min: ranges[ BREAKPOINTS.MD ][ 0 ],
				max: ranges[ BREAKPOINTS.MD ][ 1 ],
			} ),
		},
		[ BREAKPOINTS.LG ]: {
			min: 1240,
			max: 1439,
			inclusion: "[]",
			onEnter: emitSize( BREAKPOINTS.LG, {
				min: ranges[ BREAKPOINTS.LG ][ 0 ],
				max: ranges[ BREAKPOINTS.LG ][ 1 ],
			} ),
		},
		[ BREAKPOINTS.XL ]: {
			min: 1440,
			max: Infinity,
			inclusion: "[]",
			onEnter: emitSize( BREAKPOINTS.XL, {
				min: ranges[ BREAKPOINTS.XL ][ 0 ],
				max: ranges[ BREAKPOINTS.XL ][ 1 ],
			} ),
		},
	},
} );

screen.currentWidth = () => {
	return detector.width;
};
